/* eslint-disable no-console */
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode,
  HttpContextToken
} from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, throwError, of } from 'rxjs';
import { catchError, concatMap, map, tap } from 'rxjs/operators';

import { TermsOfUseItem } from '../models/terms-of-use-item.model';
import { AuthService } from '../services/api/auth.service';
import { TermsService } from '../services/api/terms.service';
import { AuthSessionService } from '../services/auth-session.service';
import { ToastService } from '../ui/components/toast';

// Context tokens to prevent infinite loops and track retry attempts
const JWT_REFRESH_ATTEMPTED = new HttpContextToken<boolean>(() => false);

// URLs that should NOT trigger JWT refresh (to prevent loops / bootstrap login)
const EXCLUDED_URLS: RegExp[] = [
  /\/assets\//i,
  /\/google/i,
  /\/login\b/i,
  /\/Login\b/i,
  /\/url\/MyWorld/i,
  /\/Cycle\/login/i
];

@Injectable()
export class JwtRefreshInterceptor implements HttpInterceptor {
  private isRefreshInProgress = false;
  private refreshObservable$: Observable<boolean> | null = null;

  constructor(
    private auth: AuthSessionService,
    private authService: AuthService,
    private termsService: TermsService,
    private toast: ToastService
  ) {}

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        // Only handle 401 errors
        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
        if (error.status !== HttpStatusCode.Unauthorized) {
          return throwError(() => error);
        }

        // Check if this request should be excluded from JWT refresh
        if (this.shouldExcludeFromRefresh(req, error)) {
          return throwError(() => error);
        }

        // Check if we already attempted refresh for this request
        if (req.context.get(JWT_REFRESH_ATTEMPTED)) {
          return this.handleFinalUnauthorized(error);
        }

        // Check if we have conditions to attempt refresh
        if (!this.canAttemptRefresh()) {
          return this.handleFinalUnauthorized(error);
        }
        return this.attemptJwtRefresh(req, next, error);
      })
    );
  }

  private shouldExcludeFromRefresh(
    req: HttpRequest<unknown>,
    error: HttpErrorResponse
  ): boolean {
    // Exclude specific URLs
    if (EXCLUDED_URLS.some((pattern) => pattern.test(req.url))) {
      return true;
    }

    // Exclude if error URL matches excluded patterns
    if (error.url && EXCLUDED_URLS.some((pattern) => pattern.test(error.url))) {
      return true;
    }

    return false;
  }

  private canAttemptRefresh(): boolean {
    const hasSession = this.auth.snapshot.isAuthenticated;
    if (!hasSession) {
      return false;
    }
    return this.authService.hasStashedToken();
  }

  private attemptJwtRefresh(
    req: HttpRequest<unknown>,
    next: HttpHandler,
    error: HttpErrorResponse
  ): Observable<HttpEvent<unknown>> {
    // If a refresh is already in progress globally, wait for it
    if (this.isRefreshInProgress && this.refreshObservable$) {
      return this.refreshObservable$.pipe(
        concatMap((success) => {
          if (success) {
            return this.retryRequestWithNewToken(req, next);
          }
          return throwError(() => error);
        })
      );
    }

    // Start a new refresh process
    this.isRefreshInProgress = true;
    const stashedToken = this.authService.peekStashedAccessToken();

    if (!stashedToken) {
      this.isRefreshInProgress = false;
      return this.handleFinalUnauthorized(error);
    }

    this.refreshObservable$ = this.performRefresh(stashedToken);

    return this.refreshObservable$.pipe(
      concatMap((success) => {
        this.isRefreshInProgress = false;
        this.refreshObservable$ = null;

        if (success) {
          return this.retryRequestWithNewToken(req, next);
        }
        return this.handleFinalUnauthorized(error);
      }),
      catchError((refreshError) => {
        this.isRefreshInProgress = false;
        this.refreshObservable$ = null;
        console.error('❌ JWT refresh failed:', refreshError);
        return this.handleFinalUnauthorized(error);
      })
    );
  }

  private performRefresh(stashedToken: string): Observable<boolean> {
    return this.authService.refreshWithToken(stashedToken).pipe(
      concatMap((userData) =>
        this.termsService.getTermsByUser().pipe(
          catchError(() => of([] as TermsOfUseItem[])),
          map((terms) => ({ userData, terms }))
        )
      ),
      tap(({ userData, terms }) => {
        this.auth.setSession(
          {
            accessToken: userData.token,
            user: {
              accessProfiles: userData.accessProfiles,
              email: userData.email,
              features: userData.features,
              id: userData.id,
              name: userData.name,
              isSpaceToBe: userData.spaceToBe
            }
          },
          userData.strapiSegments
        );
        this.auth.setTermsOfUse(terms);
        this.authService.clearStashedUrlToken();
      }),
      map(() => true),
      catchError((error) => {
        console.error('❌ JWT Refresh: Token refresh error:', error);
        return of(false);
      })
    );
  }

  private retryRequestWithNewToken(
    originalReq: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const newToken = this.auth.snapshot.accessToken;

    if (!newToken) {
      console.error('❌ JWT Refresh: No token available after refresh');
      return throwError(
        () =>
          new HttpErrorResponse({
            status: HttpStatusCode.Unauthorized,
            statusText: 'No token after refresh'
          })
      );
    }

    // Mark this request as having attempted refresh to prevent loops
    const retryReq = originalReq.clone({
      context: originalReq.context.set(JWT_REFRESH_ATTEMPTED, true),
      setHeaders: { Authorization: `Bearer ${newToken}` }
    });

    return next.handle(retryReq);
  }

  private handleFinalUnauthorized(
    error: HttpErrorResponse
  ): Observable<HttpEvent<unknown>> {
    // Entrada pelo portal: o AuthInitService ainda pode renovar com ?token=/stash.
    if (this.authService.hasPortalRecoveryToken()) {
      return throwError(() => error);
    }

    this.auth.removeSession();

    const message =
      error.error?.message || 'Sua sessão expirou, faça login novamente.';

    this.toast.danger(message, {
      id: 'jwt-session-expired'
    });

    return throwError(() => error);
  }
}
